import { useState, useMemo, useCallback } from "react";
// import "@deere/gdl-widget-v6/dist/widget.css";
// import GDLWidgetAppProd from "@deere/gdl-widget-v6/dist/gdl-widget-2_0";
import "@deere/gdl-widget-2_0/dist/widget.css";
import GDLWidgetAppProd from "@deere/gdl-widget-2_0/dist/gdl-widget-2_0";
import Typography from '@mui/material/Typography';
import { Store } from './Store';
import { ChevronRight } from './ChevronRight';
import FormSection from './components/FormSection';
import FormInput from './components/FormInput';
import FormSelect from './components/FormSelect';
import { INDUSTRY_OPTIONS, INDUSTRY_FILTERS, BOOLEAN_OPTIONS, THEME_OPTIONS, EXPERIENCE_OPTIONS, BUTTON_COLOR_OPTIONS, SEARCH_TYPE_OPTIONS } from './constants/industryOptions';
import './GDLWidgetFormNew.css';

function GDLWidgetFormNew() {
    const [inputs, setInputs] = useState({ theme: 'enterprise', experience: 'brand', gdlButtonColor: 'onLight' });
    const [selectedIndustries, setSelectedIndustries] = useState([]);
    const [userDetails, setUserDetails] = useState({});
    
    const [widgetConfig, setWidgetConfig] = useState({});
    const [widgetIndustries, setWidgetIndustries] = useState([]);
    const [widgetUserDetails, setWidgetUserDetails] = useState({});

    // Callback to handle dealer selection
    const getDealerData = useCallback((dealer) => {
        const element = document.getElementById("dealer-json");
        if (element) {
            element.innerHTML = `<strong>Dealer Selected:</strong> ${JSON.stringify(dealer, null, 2)}`;
        }
        console.log('Dealer Selected:', dealer);
    }, []);

    const startIcon = useMemo(() => <Store />, []);
    const endIcon = useMemo(() => <ChevronRight />, []);
    const sxStyle = useMemo(() => ({ width: "75%" }), []);

    const indicatorMap = useMemo(() => ({
        "sale": widgetConfig.saleRequired,
        "service": widgetConfig.serviceRequired,
        "parts": widgetConfig.partsRequired
    }), [widgetConfig.saleRequired, widgetConfig.serviceRequired, widgetConfig.partsRequired]);

    const agreementCodes = useMemo(() => 
        widgetConfig.agreementCodes 
            ? widgetConfig.agreementCodes.split(',').map(code => code.trim()).filter(code => code) 
            : undefined
    , [widgetConfig.agreementCodes]);

    const applyConfiguration = useCallback(() => {
        console.log('Applying Configuration:', inputs);
        console.log('Selected Industries:', selectedIndustries);
        console.log('User Details:', userDetails);
        setWidgetConfig({...inputs});
        setWidgetIndustries([...selectedIndustries]);
        setWidgetUserDetails({...userDetails});
    }, [inputs, selectedIndustries, userDetails]);

    const resetConfiguration = useCallback(() => {
        console.log('Resetting Configuration');
        setInputs({});
        setSelectedIndustries([]);
        setUserDetails({});
        setWidgetConfig({});
        setWidgetIndustries([]);
        setWidgetUserDetails({});
        const element = document.getElementById('dealer-json');
        if (element) element.innerHTML = '';
    }, []);

    const handleChangeSelected = (event) => {
        const value = parseInt(event.target.value);
        setSelectedIndustries(prev => 
            event.target.checked 
                ? [...prev, value] 
                : prev.filter(industry => industry !== value)
        );
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs(values => ({ ...values, [name]: value }));
    };

    const handleBooleanChange = (event) => {
        const { name, value } = event.target;
        setInputs(values => ({ ...values, [name]: value === "true" }));
    };

    const handleUserDetailsChange = (event) => {
        const { name, value } = event.target;
        setUserDetails(values => ({ ...values, [name]: value }));
    };

    return (
        <>
            <Typography variant="h5" className="gdl-form-title">
                GDL Widget 2.0
            </Typography>
            
            <div className="gdl-form-container">
                {/* Left Column - Form */}
                <div className="gdl-form-column">
                    <form>
                        {/* Location & Industry */}
                        <FormSection title="Location & Industry">
                            <FormInput
                                label="Address"
                                id="address"
                                name="address"
                                value={inputs.address}
                                onChange={handleChange}
                            />
                            <FormSelect
                                label="Industry"
                                id="productGroupId"
                                name="productGroupId"
                                value={inputs.productGroupId}
                                onChange={handleChange}
                                options={INDUSTRY_OPTIONS}
                            />
                            <FormInput
                                label="Country Code"
                                id="countryCode"
                                name="countryCode"
                                value={inputs.countryCode}
                                onChange={handleChange}
                                placeholder="e.g., US"
                            />
                            <FormInput
                                label="Locale"
                                id="locale"
                                name="locale"
                                value={inputs.locale}
                                onChange={handleChange}
                                placeholder="e.g., en_US"
                            />
                            <FormSelect
                                label="Search Type"
                                id="searchBy"
                                name="searchBy"
                                value={inputs.searchBy}
                                onChange={handleChange}
                                options={SEARCH_TYPE_OPTIONS}
                            />
                        </FormSection>

                        {/* Widget Options */}
                        <FormSection title="Widget Options">
                            <FormInput
                                label="Dealer IDs (comma-separated)"
                                id="dealerIds"
                                name="dealerIds"
                                value={inputs.dealerIds}
                                onChange={handleChange}
                                placeholder="e.g., 12345, 67890"
                            />
                            <FormInput
                                label="COG Dealer ID"
                                id="cogId"
                                name="cogId"
                                value={inputs.cogId}
                                onChange={handleChange}
                            />
                            <FormSelect
                                label="Enable COG View"
                                id="cogView"
                                name="cogView"
                                value={inputs.cogView}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Enable Location"
                                id="enableLocation"
                                name="enableLocation"
                                value={inputs.enableLocation}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Show Non-Deere Dealer"
                                id="showNonDeereDealer"
                                name="showNonDeereDealer"
                                value={inputs.showNonDeereDealer}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Show Purchase Parts"
                                id="showPurchaseParts"
                                name="showPurchaseParts"
                                value={inputs.showPurchaseParts}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Preselect Dealer"
                                id="enablePreselectDealerData"
                                name="enablePreselectDealerData"
                                value={inputs.enablePreselectDealerData}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Enable Core Media"
                                id="isCoreMediaEnabled"
                                name="isCoreMediaEnabled"
                                value={inputs.isCoreMediaEnabled}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Theme"
                                id="theme"
                                name="theme"
                                value={inputs.theme}
                                onChange={handleChange}
                                options={THEME_OPTIONS}
                            />
                            <FormSelect
                                label="Experience"
                                id="experience"
                                name="experience"
                                value={inputs.experience}
                                onChange={handleChange}
                                options={EXPERIENCE_OPTIONS}
                            />
                            <FormSelect
                                label="Button Color"
                                id="gdlButtonColor"
                                name="gdlButtonColor"
                                value={inputs.gdlButtonColor}
                                onChange={handleChange}
                                options={BUTTON_COLOR_OPTIONS}
                            />
                            <FormInput
                                label="Environment"
                                id="env"
                                name="env"
                                value={inputs.env}
                                onChange={handleChange}
                                placeholder="e.g., cert"
                            />
                            <FormInput
                                label="Dog ID"
                                id="dogId"
                                name="dogId"
                                value={inputs.dogId}
                                onChange={handleChange}
                                placeholder="e.g., 201010"
                            />
                        </FormSection>

                        {/* Product & E-commerce */}
                        <FormSection title="Product & E-commerce">
                            <FormInput
                                label="Product Code"
                                id="productCode"
                                name="productCode"
                                value={inputs.productCode}
                                onChange={handleChange}
                            />
                            <FormInput
                                label="Product Name"
                                id="productName"
                                name="productName"
                                value={inputs.productName}
                                onChange={handleChange}
                                placeholder="e.g., S100 Lawn Tractor"
                            />
                            <FormInput
                                label="Quantity"
                                id="quantity"
                                name="quantity"
                                type="number"
                                value={inputs.quantity}
                                onChange={handleChange}
                            />
                            <FormInput
                                label="Agreement Codes (comma-separated)"
                                id="agreementCodes"
                                name="agreementCodes"
                                value={inputs.agreementCodes}
                                onChange={handleChange}
                            />
                        </FormSection>

                        {/* User Details */}
                        <FormSection title="User Details">
                            <FormInput
                                label="User ID / Email"
                                id="userId"
                                name="userId"
                                value={userDetails.userId}
                                onChange={handleUserDetailsChange}
                                placeholder="user@johndeere.com"
                            />
                            <FormInput
                                label="First Name"
                                id="firstName"
                                name="firstName"
                                value={userDetails.firstName}
                                onChange={handleUserDetailsChange}
                            />
                            <FormInput
                                label="Last Name"
                                id="lastName"
                                name="lastName"
                                value={userDetails.lastName}
                                onChange={handleUserDetailsChange}
                            />
                            <FormInput
                                label="Email"
                                id="email"
                                name="email"
                                type="email"
                                value={userDetails.email}
                                onChange={handleUserDetailsChange}
                            />
                            <FormInput
                                label="Phone Number"
                                id="phoneNumber"
                                name="phoneNumber"
                                type="tel"
                                value={userDetails.phoneNumber}
                                onChange={handleUserDetailsChange}
                            />
                        </FormSection>

                        {/* Dealer Requirements */}
                        <FormSection title="Dealer Requirements">
                            <FormSelect
                                label="Sales Required"
                                id="saleRequired"
                                name="saleRequired"
                                value={inputs.saleRequired}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Parts Required"
                                id="partsRequired"
                                name="partsRequired"
                                value={inputs.partsRequired}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Service Required"
                                id="serviceRequired"
                                name="serviceRequired"
                                value={inputs.serviceRequired}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                            <FormSelect
                                label="Parts Email Required"
                                id="partsDealerEmailRequired"
                                name="partsDealerEmailRequired"
                                value={inputs.partsDealerEmailRequired}
                                onChange={handleBooleanChange}
                                options={BOOLEAN_OPTIONS}
                            />
                        </FormSection>

                        {/* Industry Filter */}
                        <FormSection title="Industry Filter">
                            <div className="gdl-checkbox-grid">
                                {INDUSTRY_FILTERS.map((industry) => (
                                    <label key={industry.value} className="gdl-checkbox-label">
                                        <input
                                            type="checkbox"
                                            value={industry.value}
                                            onChange={handleChangeSelected}
                                        />
                                        {industry.label}
                                    </label>
                                ))}
                            </div>
                        </FormSection>

                        {/* Action Buttons */}
                        <div className="gdl-action-buttons">
                            <button 
                                type="button" 
                                onClick={applyConfiguration} 
                                className="gdl-btn gdl-btn-apply"
                            >
                                ✓ Apply
                            </button>
                            <button 
                                type="button" 
                                onClick={resetConfiguration} 
                                className="gdl-btn gdl-btn-reset"
                            >
                                ✕ Reset
                            </button>
                        </div>
                    </form>
                </div>

                {/* Right Column - Widget Display */}
                <div className="gdl-widget-column">
                    <div className="gdl-widget-preview">
                        <Typography className="gdl-widget-preview-title">
                            Widget Preview
                        </Typography>
                        <GDLWidgetAppProd
                            key="widget-primary"
                            getDealerData={getDealerData}
                            variant="primary"
                            startIcon={startIcon}
                            endIcon={endIcon}
                            buttonText="Fuel React Button"
                            isCoreMediaEnabled={widgetConfig.isCoreMediaEnabled}
                            enablePreselectDealerData={widgetConfig.enablePreselectDealerData}
                            editbutton={true}
                            sx={sxStyle}
                            countryCode={widgetConfig.countryCode}
                            locale={widgetConfig.locale}
                            address={widgetConfig.address}
                            productGroupId={widgetConfig.productGroupId ? parseInt(widgetConfig.productGroupId) : undefined}
                            dealerIds={widgetConfig.dealerIds}
                            partsDealerEmailRequired={widgetConfig.partsDealerEmailRequired}
                            showNonDeereDealer={widgetConfig.showNonDeereDealer}
                            filteredIndustries={widgetIndustries}
                            cogView={widgetConfig.cogView}
                            cogId={widgetConfig.cogId}
                            productCode={widgetConfig.productCode}
                            quantity={widgetConfig.quantity ? parseInt(widgetConfig.quantity) : undefined}
                            indicatorMap={indicatorMap}
                            enableLocation={widgetConfig.enableLocation}
                            showPurchaseParts={widgetConfig.showPurchaseParts}
                            agreementCodes={agreementCodes}
                            userDetails={Object.keys(widgetUserDetails).length > 0 ? widgetUserDetails : undefined}
                            env={widgetConfig.env}
                            dogId={widgetConfig.dogId}
                            productName={widgetConfig.productName}
                            theme={widgetConfig.theme || undefined}
                            experience={widgetConfig.experience || undefined}
                            gdlButtonColor={widgetConfig.gdlButtonColor || undefined}
                            searchBy={widgetConfig.searchBy}
                            
                        />
                        <div id="dealer-json" className="gdl-dealer-json"></div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default GDLWidgetFormNew;
